// simple react hook component that only displays a dialog with a label and a button in it

import React, {useState, useCallback, useRef, useEffect} from 'react';
import {Button} from "devextreme-react/button";
import './new-gpt.scss';
import {TextBox, Button as TextBoxButton} from "devextreme-react/text-box";
import notify from 'devextreme/ui/notify';
import FileUploader, {FileUploaderTypes} from 'devextreme-react/file-uploader';
import TabPanel from 'devextreme-react/tab-panel';
import {ProgressBar} from 'devextreme-react/progress-bar';
import TextArea from 'devextreme-react/text-area';
import {apiUrl, useApi} from "../../api/api-methods";
import Gallery from "devextreme-react/gallery";
import {fileTypeIcon} from "../../utils/file-type-icons";
import {formatBytes} from "../../utils/utils.num";
import useStateCallback from '../../utils/useStateCallbackHook';
import DropDownBox from 'devextreme-react/drop-down-box';
import DataGrid, {
  Selection, Paging, FilterRow, Scrolling, Column, Editing
} from 'devextreme-react/data-grid';
import {confirm} from 'devextreme/ui/dialog';
import {useAuth} from "../../contexts/auth";
import msalInstance from "../../api/authConfig";
import { Client } from "@microsoft/microsoft-graph-client";

const allowedFileExtensions = ['.pdf', '.docx', '.pptx', '.xlsx'];
const maxFileSize = 1024 * 1024 * 50; // 50MB
const tabsDataSource = [
  {title: 'Access', icon: 'doc'},
  {title: 'Training Documents', icon: 'doc'},
  {title: 'Ownership', icon: 'user'}
];

export default function NewGPT(props) {
  const [gptName, setGptName] = useState('');
  const [gptDescription, setGptDescription] = useState('');
  const fileUploader = useRef(null);
  const [files, setFiles] = useState([]);
  const [contextId, setContextId] = useStateCallback(null);
  const [progressVisible, setProgressVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMax, setProgressMax] = useState(0);
  const [allUsers, setAllUsers] = useState([]);
  const [shareWithUserIds, setShareWithUserIds] = useState([]);
  const [ownerUserIds, setOwnerUserIds] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]); // for editing existing GPT
  const [saveBtnLabel, setSaveBtnLabel] = useState('Save GPT'); // Save GPT | Save & Retrain GPT
  const [sharePointFolderURL, setSharePointFolderURL] = useState('');
  const [sharepointFetchProgress, setSharepointFetchProgress] = useState(0);
  const [sharePointFetchMax, setSharePointFetchMax] = useState(0);
  const api = useApi();
  const {user} = useAuth();

  function onInit() {
    async function asyncSpace() {
      await onDataNeedsUpdate();
    }

    void asyncSpace();
  }

  async function onDataNeedsUpdate() {
    const users = await api.user.getAll();
    const allDocuments = props.selectedGpt ? await api.context.getAllDocuments(props.selectedGpt.id) : [];
    setAllUsers(users);
    setExistingDocuments(allDocuments);
    setProgressVisible(false);
    setOwnerUserIds([user.id]);
    setSaveBtnLabel('Save GPT');
  }

  const onGptNameChanged = useCallback((e) => {
    setGptName(e.value);
  }, [setGptName]);

  const onGptDescriptionChanged = useCallback((e) => {
    setGptDescription(e.value);
  }, [setGptDescription]);

  const dataGridOnSelectionChanged = useCallback((e) => {
    console.log('dataGridOnSelectionChanged', e.selectedRowKeys);
    setShareWithUserIds((e.selectedRowKeys.length && e.selectedRowKeys) || []);
  }, []);

  const dataGridOnOwnersSelectionChanged = useCallback((e) => {
    console.log('dataGridOnOwnersSelectionChanged', e.selectedRowKeys);
    const newValue = (e.selectedRowKeys.length && e.selectedRowKeys) || [];
    confirm("Changing user will set the new owner and you will no longer be able to manage this GPT model. Are you sure?", "Ownership confirmation")
      .then(confirmed => {
        if (!confirmed) return;
        setOwnerUserIds(newValue);
      })
  }, []);

  const dataGridRender = useCallback(() => (
      <DataGrid
        height={345}
        dataSource={allUsers}
        keyExpr="id"
        displayExpr="name"
        hoverStateEnabled={true}
        selectedRowKeys={shareWithUserIds}
        onSelectionChanged={dataGridOnSelectionChanged}
      >
        <Selection mode="multiple"/>
        <Scrolling mode="virtual"/>
        <Paging
          enabled={true}
          pageSize={10}
        />
        <FilterRow visible={true}/>

        <Column dataField={'id'} dataType="number" width={90}/>
        <Column dataField={'name'} dataType="string"/>
        <Column dataField={'email'} dataType="string"/>
      </DataGrid>
    ),
    [allUsers, shareWithUserIds],
  );

  const ownersDataGridRender = useCallback(() => (
      <DataGrid
        height={345}
        dataSource={allUsers}
        keyExpr="id"
        displayExpr="name"
        hoverStateEnabled={true}
        selectedRowKeys={ownerUserIds}
        onSelectionChanged={dataGridOnOwnersSelectionChanged}
      >
        <Selection mode="single"/>
        <Scrolling mode="virtual"/>
        <Paging
          enabled={true}
          pageSize={10}
        />
        <FilterRow visible={true}/>

        <Column dataField={'id'} dataType="number" width={90}/>
        <Column dataField={'name'} dataType="string"/>
        <Column dataField={'email'} dataType="string"/>
      </DataGrid>
    ),
    [allUsers, shareWithUserIds],
  );

  const syncDataGridSelection = useCallback((e) => {
    setShareWithUserIds(e.value || []);
  }, []);

  const syncOwnersDataGridSelection = useCallback((e) => {
    setOwnerUserIds(e.value || []);
  }, []);

  const syncSharePointFolderURLChange = useCallback((e) => {
    console.log('syncSharePointFolderURLChange', e);
    setSharePointFolderURL(e.value || '');
  }, []);

  const fileUpdate = useCallback(({file, request}) => {
    const update = !request.status
      ? {status: 'Uploading...', src: URL.createObjectURL(file)}
      : request.status >= 299
        ? {success: false, status: `Upload failed: ${request.response}`}
        : {success: true, uploadId: request.response, status: 'Uploaded', uploaded: true};
    Object.assign(file, update);
    setFiles([...files]);
  }, [files]);

  const renderAttachment = useCallback((file, ix) => {
    const {name, size, type, src, uploadId, status, success, uploaded} = file;
    const ext = name.split('.').pop();
    const canDelete = !!uploadId || success === false;
    const onClick = () => setFiles([...files.slice(0, ix), ...files.slice(ix + 1)]);
    const image = src && type && type.startsWith('image/');
    return (<div key={src || uploadId || name} className={"attachment" + (success ? " success" : " error")}>
      <div className="attachment-icon">
        {!image && success && <a href={src} download={name}><i className={fileTypeIcon(type)}/></a>}
        {!success && uploaded && <i className="dx-icon dx-icon-warning"/>}
        {!success && !uploaded && <i className={`dx-icon dx-icon-${ext}file`}/>}
      </div>
      <div className="attachment-desc">
        <div className="attachment-name">{name}</div>
        <div className="attachment-size">{formatBytes(size)}</div>
        <div className="attachment-status">{status}</div>
        {canDelete && <Button icon="close" className="close-icon" onClick={onClick}/>}
      </div>
    </div>);
  }, [files]);

  async function onFilesValueChanged(e) {
    for (const file of e.value) {
      // if (file.status) continue;
      Object.assign(file, {status: 'Queued', success: false, uploaded: false});
    }
    setFiles(e.value);
    if (props.selectedGpt) setSaveBtnLabel('Save & Retrain GPT');
  }

  async function getAccessData() {
    if (!props.selectedGpt) return;
    const result = await api.context.getAccess(props.selectedGpt.id);
    const gridValues = result.map((item) => item.userId);
    setShareWithUserIds(gridValues);
  }

  async function upsertAccessData(ctxId) {
    const accessData = shareWithUserIds.map((item) => {
      return {userId: item, contextId: ctxId, permission: 1, grantedBy: user.id};
    });
    console.log('upsertAccessData', accessData);
    await api.context.upsertAccess(ctxId, accessData);
  }

  async function createContext(callback) {
    const requestBody = {name: gptName, description: gptDescription};
    const result = await api.context.createAndGet(requestBody);
    console.log('api.context.createAndGet', requestBody, result);
    if (result.id) setContextId(result.id, callback);
  }

  async function uploadSingleFile(file, ctxId) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.context.uploadDocument(ctxId, formData);
    console.log('api.context.uploadDocument', contextId, ctxId, formData, res);
  }

  function onExistingDocsRemoved(e) {
    console.log('onExistingDocsRemoved', e, existingDocuments);
    if (props.selectedGpt) setSaveBtnLabel('Save & Retrain GPT');
  }

  function validateForm(ignoreFiles = false) {
    if (!gptName || !gptDescription) {
      notify('GPT name and description is required', 'error', 2500);
      return false;
    }
    if (!files.length && !ignoreFiles) {
      notify('Please select files to upload', 'error', 2500);
      return false;
    }
    return true;
  }

  async function updateOwnership() {
    const newUserId = ownerUserIds[0];
    const res = await api.context.updateOwnership(props.selectedGpt.id, newUserId);
    console.log('updateOwnership', res);
  }

  async function createGpt() {
    if (!validateForm()) return;
    setProgressVisible(true);
    setProgress(0);
    await createContext(async (ctxId) => {
      console.log('createGpt ctx callback', ctxId, files);
      for (const file of files) {
        if (file.uploaded) continue;
        await uploadSingleFile(file, ctxId);
        setProgress((prevState) => prevState + 1);
      }
      await upsertAccessData(ctxId);
      notify(`GPT ${gptName} created successfully`, 'success', 2500);
      props.dataFetchFn();
      props.popupVisibilityFn(false);
    });
  }

  async function modifyTrainingFiles() {
    // 1. We need to first figure out existing documents that were deleted, so fetch all documents again and compare
    // 2. When we have a list of existing documents we know we can delete - let's delete them one by one
    // 3. Then we can upload new files

    const freshExistingDocuments = await api.context.getAllDocuments(props.selectedGpt.id);
    const existingDocumentIds = existingDocuments.map((item) => item.id);
    const freshDocumentIds = freshExistingDocuments.map((item) => item.id);
    const deletedDocuments = freshDocumentIds.filter((item) => !existingDocumentIds.includes(item));
    console.log('deletedDocuments', deletedDocuments);

    setProgress(0);
    setProgressMax(deletedDocuments.length + files.length);
    // 1. Delete all deleted documents
    for (const docId of deletedDocuments) {
      await api.context.deleteDocument(props.selectedGpt.id, docId);
      setProgress((prevState) => prevState + 1);
    }

    // 2. Upload new files
    for (const file of files) {
      if (file.uploaded) continue;
      await uploadSingleFile(file, props.selectedGpt.id);
      setProgress((prevState) => prevState + 1);
    }
  }

  async function saveGpt() {
    if (!validateForm(true)) return;
    setProgressVisible(true);
    await modifyTrainingFiles();
    await upsertAccessData(props.selectedGpt.id);
    await api.context.updateContext(props.selectedGpt.id, {name: gptName, description: gptDescription});
    if (ownerUserIds.length && ownerUserIds[0] !== user.id) await updateOwnership();
    notify('GPT saved successfully', 'success', 2500);
    props.dataFetchFn();
    props.popupVisibilityFn(false);
  }

  function statusFormat(ratio) {
    return `Processed: ${ratio * 100}%`;
  }

  function calculateBytesColumnValue(rowData) {
    return formatBytes(rowData.size);
  }

  async function onFetchSharePointClick() {
    console.log('onFetchSharePointClick 2', sharePointFolderURL);
    try {
      await msalInstance.initialize();
      const tokenResponse = await msalInstance.acquireTokenSilent({
        scopes: ["Files.Read", "Sites.Read.All"],
      });

      const graphClient = Client.init({
        authProvider: (done) => {
          done(null, tokenResponse.accessToken);
        },
      });

      // Parse the SharePoint URL
      const url = new URL(sharePointFolderURL);
      const hostname = url.hostname; // e.g., aspectali.sharepoint.com
      let pathname = decodeURIComponent(url.pathname);

      // Remove any query parameters or fragments
      pathname = pathname.split("?")[0].split("#")[0];

      // Remove '/Forms/AllItems.aspx' if present
      pathname = pathname.replace(/\/Forms\/AllItems\.aspx$/i, "");

      // Split the path into segments
      const pathSegments = pathname.split("/").filter(Boolean);

      // Find the 'sites' segment to get the site path
      const sitesIndex = pathSegments.indexOf("sites");
      if (sitesIndex === -1 || sitesIndex + 1 >= pathSegments.length) {
        throw new Error("Invalid SharePoint site URL.");
      }

      const sitePath = pathSegments[sitesIndex + 1];

      // Get the folder path after 'sites/{site-name}'
      const folderPathSegments = pathSegments.slice(sitesIndex + 2);

      // Remove 'Shared Documents' from the folder path, since it's the root of the drive
      if (folderPathSegments[0] === "Shared Documents") {
        folderPathSegments.shift(); // Remove 'Shared Documents'
      }

      let folderPath = "/" + folderPathSegments.join("/"); // Ensure it starts with '/'

      console.log('Parsed values:', { hostname, sitePath, folderPath });

      // Get the site ID
      const site = await graphClient.api(`/sites/${hostname}:/sites/${sitePath}`).get();
      const siteId = site.id;

      // Get the default drive (which corresponds to 'Shared Documents')
      const drive = await graphClient.api(`/sites/${siteId}/drive`).get();
      const driveId = drive.id;

      let folderContents;

      if (folderPath === "/") {
        // If folderPath is root, fetch the root children
        folderContents = await graphClient
          .api(`/drives/${driveId}/root/children`)
          .get();
      } else {
        // Fetch the contents of the specified folder
        folderContents = await graphClient
          .api(`/drives/${driveId}/root:${folderPath}:/children`)
          .get();
      }

      console.log('folderContents', folderContents);
      setSharePointFetchMax(folderContents.value.length);
      // Initialize an array to hold the File objects
      const fileObjects = [];

      // Iterate over each file metadata and download the file
      for (const fileMetadata of folderContents.value) {
        // Skip if it's a folder
        if (fileMetadata.folder) continue;

        const fileUrl = fileMetadata['@microsoft.graph.downloadUrl'];
        const fileName = fileMetadata.name;

        // Download the file content
        const response = await fetch(fileUrl);
        const blob = await response.blob();

        // Create a File object from the Blob
        const file = new File([blob], fileName, { type: blob.type });

        // Assign custom properties if needed
        Object.assign(file, {
          status: 'Queued',
          success: false,
          uploaded: false,
        });

        // Add the File object to the array
        fileObjects.push(file);
        setSharepointFetchProgress((prevState) => prevState + 1);
      }

      // Update the state with the new files
      setFiles(fileObjects);
      setSharepointFetchProgress(0);
      setSharePointFetchMax(0);
    } catch (error) {
      console.error("Error fetching files: ", error);
    }
  }

  function TabPanelItem(e) {
    console.log('TabPanelItem', e);
    switch (e.index) {
      case 0: // Access
        return <div className="vertical-paddings">
          <label>Share with</label>
          <DropDownBox
            value={shareWithUserIds}
            valueExpr="id"
            deferRendering={false}
            displayExpr="name"
            placeholder="Select users..."
            showClearButton={true}
            dataSource={allUsers}
            onValueChanged={syncDataGridSelection}
            contentRender={dataGridRender}
          />
          <small>Leave empty to share with everyone</small>
        </div>;
      case 1: // Training Documents
        return <div className="tabpanel-item">
          <div className="vertical-paddings" style={{display: 'flex', width: '100%', alignItems: 'center'}}>
            <Button id={'gpts-select-files-button'}
                    icon="attach"
                    type="default"
                    width={'250px'}
                    text={!props.selectedGpt ? 'Select Files' : 'Select Additional Files'}
            />
            <span style={{marginLeft: '2rem'}}>OR</span>
            <div style={{display: 'inline-block', marginLeft: '2rem', marginRight: '2rem'}}>
              <TextBox value={sharePointFolderURL}
                       label={'SharePoint Folder URL'}
                       labelMode="floating"
                       stylingMode="outlined"
                       fullWidth={false}
                       style={{width: '350px'}}
                       onValueChanged={syncSharePointFolderURLChange}>
                <TextBoxButton
                  name="password"
                  location="after"
                  options={{
                    icon: 'import', stylingMode: 'contained', type: 'default',
                    text: 'Fetch', onClick: onFetchSharePointClick
                  }}
                />
              </TextBox>
            </div>
            <div style={{display: 'inline-block', width: '100%'}}>
              <ProgressBar
                min={0}
                max={sharePointFetchMax}
                value={sharepointFetchProgress}
                visible={sharePointFetchMax > 0}
              />
            </div>
          </div>
          <FileUploader
            ref={fileUploader}
            visible={false}
            dialogTrigger={'#gpts-select-files-button'}
            accept={allowedFileExtensions.join(',')}
            maxFileSize={maxFileSize}
            uploadMode="useButtons"
            uploadUrl={apiUrl(`context/upload/${contextId}`)}
            multiple={true}
            onValueChanged={onFilesValueChanged}
            onUploadStarted={fileUpdate}
            onUploadError={fileUpdate}
            onUploaded={fileUpdate}
            uploadHeaders={{Authorization: `Bearer ${api.token}`}}
            showFileList={false}
            value={files}
          />
          {files.length > 0 && (
            <div className="attachments">
              <Gallery
                items={files}
                height={120}
                width={'100%'}
                initialItemWidth={300}
                loop={false}
                focusStateEnabled={false}
                showIndicator={false}
                showNavButtons={true}
                itemRender={renderAttachment}
              />
            </div>)}
          {!!props.selectedGpt && <div>
            <div className="vertical-paddings">Existing training documents:</div>
            <DataGrid dataSource={existingDocuments} keyExpr="id" height={300} onRowRemoved={onExistingDocsRemoved}>
              <Editing mode="row" allowDeleting={true}/>
              <Column dataField={'id'} dataType="number" width={90}/>
              <Column dataField={'displayName'} dataType="string"/>
              <Column dataField={'size'} dataType="number" caption="Size (Bytes)"
                      calculateDisplayValue={calculateBytesColumnValue}/>
              <Column dataField={'created'} dataType="string"/>
            </DataGrid>
          </div>}
        </div>;
      case 2: // Ownership
        return <div className="vertical-paddings">
          <label>GPT owner</label>
          <DropDownBox
            value={ownerUserIds}
            valueExpr="id"
            deferRendering={false}
            displayExpr="name"
            placeholder="Select new owner.."
            showClearButton={true}
            dataSource={allUsers}
            onValueChanged={syncOwnersDataGridSelection}
            contentRender={ownersDataGridRender}
            disabled={!props.selectedGpt}
          />
          <small>Setting an user different than yourself will transfer ownership</small>
        </div>;
      default:
        return <div>Nothing to see here</div>;
    }
  }

  useEffect(() => {
    if (props.selectedGpt) {
      setGptName(props.selectedGpt.name);
      setGptDescription(props.selectedGpt.description);
    } else {
      setGptName('');
      setGptDescription('');
    }
    void getAccessData();
    void onDataNeedsUpdate();
  }, [props.selectedGpt, props.popupVisible]);

  useEffect(onInit, []);

  return (
    <React.Fragment>
      <div className="new-gpt">
        <div>
          <TextBox value={gptName}
                   label={'GPT Name'}
                   labelMode="floating"
                   stylingMode="outlined"
                   onValueChanged={onGptNameChanged}/>
          <br/>
          <TextArea value={gptDescription}
                    label={'GPT Instruction'}
                    labelMode="floating"
                    stylingMode="outlined"
                    height={100}
                    onValueChanged={onGptDescriptionChanged}/>
          <small>Example: You are a helpful assistant. Answer as accurately as possible using the supplied
            context.</small>
        </div>
        <div>
          <div className="vertical-paddings">
            <TabPanel
              width="100%"
              animationEnabled={true}
              swipeEnabled={true}
              dataSource={tabsDataSource}
              stylingMode="primary"
              iconPosition="start"
              itemComponent={TabPanelItem}
            />
          </div>
          <hr/>
          <div className="vertical-paddings">
            <Button type="default"
                    stylingMode="contained"
                    visible={!props.selectedGpt}
                    text="Create GPT"
                    onClick={createGpt}/>
            <Button type="default"
                    stylingMode="contained"
                    visible={!!props.selectedGpt}
                    text={saveBtnLabel}
                    onClick={saveGpt}/>
          </div>
          <div className="vertical-paddings">
            <ProgressBar
              min={0}
              max={progressMax}
              value={progress}
              visible={progressVisible}
              statusFormat={statusFormat}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}